/* eslint-disable css-modules/no-undef-class, css-modules/no-unused-class, no-nested-ternary */
import { ChangeEvent, FC, useState, FocusEvent } from 'react';
import { TextField, Theme } from '@mui/material';
import { SimpleLoader, Tooltip } from 'common-components';
import { NicknameProps } from './nicknameTypes';
import { InputAdornment, CheckIcon, createStyles, makeStyles } from '../uiLibraryImports';
import { useTranslationContext } from '../contexts/translationContext';
import styles from './nickname.module.scss';

const Nickname: FC<NicknameProps> = (props: NicknameProps) => {
    const {
        nickName,
        isValidNickname,
        onChange,
        error,
        errorMessage,
        helperText,
        autofocus,
        onBlurFunction,
        handleCancel,
        isValidating
    } = props;
    const { translate } = useTranslationContext();
    const [text, setText] = useState<string>(nickName);

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const { value } = e.target;
        setText(value);
        onChange(value);
    };

    const ValidateTooltip = () => (
        <div className={styles.tooltip}>
            <div className={styles['tooltip-text']}>{translate('nicknameTooltipText')}</div>
            <div className={styles.cancel} onClick={() => handleCancel()}>
                {translate('Cancel')}
            </div>
        </div>
    );

    const useStyles = makeStyles((_: Theme) =>
        createStyles({
            cssOutlinedInput: {
                height: '2.75rem !important',
                width: '100% !important',
                background: 'rgba(38, 35, 58, 0.5)',
                color: 'white',
                '&$cssFocused $notchedOutline': {
                    borderColor: error ? 'rgba(241, 96, 93, 1) !important' : 'rgba(71, 154, 235, 1) !important'
                },
                '&:hover fieldset': {
                    borderColor: error ? 'rgba(241, 96, 93, 1) !important' : 'rgba(255, 255, 255, 0.7) !important'
                }
            },
            cssFocused: {},
            notchedOutline: {
                borderColor: error ? 'rgba(241, 96, 93, 1) !important' : 'rgba(255, 255, 255, 0.25) !important'
            },
            helperText: {
                textAlign: 'left',
                marginTop: 0,
                color: error ? 'rgba(241, 96, 93, 1) !important' : 'rgba(255, 255, 255, 0.50) !important',
                marginLeft: '0 !important'
            }
        })
    );
    const classes = useStyles();

    return (
        <TextField
            id="nickname"
            className={styles.nickname}
            type="text"
            value={text}
            autoFocus={autofocus}
            helperText={errorMessage || helperText}
            onChange={handleOnChange}
            FormHelperTextProps={{
                className: classes.helperText
            }}
            placeholder={translate('nicknamePlaceHolder')}
            onBlur={(_: FocusEvent<HTMLInputElement>): void => onBlurFunction?.(text)}
            variant="outlined"
            inputProps={{
                maxLength: 40,
                'data-testid': 'nickname'
            }}
            InputProps={{
                classes: {
                    root: classes.cssOutlinedInput,
                    focused: classes.cssFocused,
                    notchedOutline: classes.notchedOutline
                },
                endAdornment: (
                    <InputAdornment position="end">
                        {isValidNickname ? (
                            <CheckIcon htmlColor="#4DBB40" />
                        ) : isValidating ? (
                            <Tooltip
                                dataTestid="loader-tooltip"
                                title={<ValidateTooltip />}
                                placement="right"
                                interactive
                            >
                                <span className={styles['icon-loader']}>
                                    <SimpleLoader dataTestid="validating-loader" color="inherit" size="13px" />
                                </span>
                            </Tooltip>
                        ) : undefined}
                    </InputAdornment>
                )
            }}
        />
    );
};

export default Nickname;
